'use client'

import React, { useState, useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import {
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
} from 'lucide-react'

interface TableData {
  headers: string[]
  rows: string[][]
  csvUrl: string
  caption?: string
}

interface TableRendererProps {
  data: TableData
  className?: string
}

export function TableRenderer({ data, className }: TableRendererProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  // Convert rows to object format for TanStack Table
  const tableData = useMemo(() => {
    return data.rows.map(row => {
      const rowObj: Record<string, string> = {}
      data.headers.forEach((header, index) => {
        rowObj[header] = row[index] || ''
      })
      return rowObj
    })
  }, [data])

  // Define columns
  const columns = useMemo<ColumnDef<any>[]>(() => {
    return data.headers.map(header => ({
      accessorKey: header,
      header: ({ column }) => {
        return (
          <button
            className="flex items-center gap-1 font-semibold hover:text-blue-600 transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {header}
            <ArrowUpDown className="w-3 h-3" />
          </button>
        )
      },
      cell: info => info.getValue(),
      enableSorting: true,
      enableColumnFilter: true,
    }))
  }, [data.headers])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handleDownloadCSV = () => {
    window.open(data.csvUrl, '_blank')
  }

  return (
    <div
      className={`table-container my-4 rounded-lg border bg-white flex flex-col ${className || ''}`}
      style={{
        width: '100%',
        minWidth: 'min(950px, 100%)',
        maxWidth: '100%',
        maxHeight: '600px',
      }}
    >
      {/* Header with caption and download button */}
      <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3 flex-shrink-0">
        <div>
          {data.caption && (
            <h3 className="text-sm font-semibold text-gray-900">
              {data.caption}
            </h3>
          )}
          <p className="text-xs text-gray-500">{tableData.length} total rows</p>
        </div>
        <button
          onClick={handleDownloadCSV}
          className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download CSV
        </button>
      </div>

      {/* Global search bar */}
      <div className="border-b bg-gray-50 px-4 py-2 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={globalFilter ?? ''}
            onChange={e => setGlobalFilter(e.target.value)}
            placeholder="Search all columns..."
            className="w-full rounded-md border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Table - Responsive with vertical scroll */}
      <div
        className="overflow-x-auto overflow-y-auto flex-1"
        style={{ maxHeight: '400px' }}
      >
        <table className="w-full text-sm">
          <thead className="bg-gray-100 border-b sticky top-0">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {table.getRowModel().rows.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No data available
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between border-t bg-gray-50 px-4 py-3 flex-shrink-0">
        <div className="text-xs text-gray-700">
          Showing{' '}
          <span className="font-medium">
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}
          </span>{' '}
          to{' '}
          <span className="font-medium">
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              tableData.length
            )}
          </span>{' '}
          of <span className="font-medium">{tableData.length}</span> results
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-700">
            Page{' '}
            <span className="font-medium">
              {table.getState().pagination.pageIndex + 1}
            </span>{' '}
            of <span className="font-medium">{table.getPageCount()}</span>
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
